import {
    Component,
    Inject,
    OnInit,
    PLATFORM_ID,
    Renderer2,
    RendererFactory2
} from '@angular/core';
import {isPlatformBrowser, registerLocaleData} from "@angular/common";
import {BehaviorSubject, filter} from "rxjs";
import {ActivatedRouteSnapshot, NavigationEnd, Router} from "@angular/router";
import it from '@angular/common/locales/it';
import {CompanyService} from "./services/company/company.service";
import {Company} from "./models/company";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";
import {environment} from "./environments/environment";

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
    title = 'Well Done';
    static isBrowser = new BehaviorSubject<boolean>(false);
    useCustomLayout: boolean = false;
    shopLayout: boolean = false;
    fullBleed: boolean = false;
    company: Company = new Company();

    constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        public router: Router,
        private companyService: CompanyService,
        private sanitizer: DomSanitizer,
    ) {
        registerLocaleData(it);
        AppComponent.isBrowser.next(isPlatformBrowser(platformId))
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(() => this.syncLayoutFromRouter());
        queueMicrotask(() => this.syncLayoutFromRouter());
    }

    /**
     * Route `data` lives on parent segments (e.g. companies has customLayout) while leaf segments
     * often have empty data. Reading only the leaf ActivationEnd cleared the layout and swapped
     * between two root-level router-outlets, which broke in-app navigation (blank shell / stuck URL).
     */
    private syncLayoutFromRouter(): void {
        let leaf: ActivatedRouteSnapshot = this.router.routerState.snapshot.root;
        while (leaf.firstChild) {
            leaf = leaf.firstChild;
        }
        let custom: boolean | undefined;
        let shop: boolean | undefined;
        let bleed: boolean | undefined;
        let r: ActivatedRouteSnapshot | null = leaf;
        while (r) {
            if (custom === undefined && r.data['customLayout'] !== undefined) {
                custom = !!r.data['customLayout'];
            }
            if (shop === undefined && r.data['shopLayout'] !== undefined) {
                shop = !!r.data['shopLayout'];
            }
            if (bleed === undefined && r.data['fullBleed'] !== undefined) {
                bleed = !!r.data['fullBleed'];
            }
            r = r.parent;
        }
        this.useCustomLayout = custom ?? false;
        this.shopLayout = shop ?? false;
        this.fullBleed = bleed ?? false;
    }

    async ngOnInit() {
        if (!environment.production && window.location.pathname === '/demo/partial-payment') {
            return;
        }

        const companyShop = await this.companyService.getCompanyFromShopDomain(window.location.hostname);
        if (companyShop) {
            if (companyShop.iubendaScript) this.injectCookieScripts(companyShop.iubendaScript);
            await this.router.navigate(['/shop']);
        }

        const companyId = localStorage.getItem('wisper_companyId') ?? '';
        this.company = (await this.companyService.getCompanyById(companyId)).data;
    }

    injectCookieScripts(htmlString: string) {
        const container = document.createElement('div');
        container.innerHTML = this.sanitizer.bypassSecurityTrustHtml(htmlString).toString();

        const scripts = container.querySelectorAll('script');

        scripts.forEach(originalScript => {
            const script = document.createElement('script');
            Array.from(originalScript.attributes).forEach(attr => {
                script.setAttribute(attr.name, attr.value);
            });
            if (originalScript.innerHTML) {
                script.innerHTML = originalScript.innerHTML;
            }

            document.head.appendChild(script);
        })
    }
}
